"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useDataflowOfZoom = void 0;
var tslib_1 = require("tslib");
var operators_1 = require("rxjs/operators");
var utils_1 = require("../../../utils");
var wheel_scroll_delta_1 = require("../../configs/wheel-scroll-delta");
var img_move_reducer_1 = require("../../functions/reducer/img-move-reducer");
var zoom_ratio_reducer_1 = require("../../functions/reducer/zoom-ratio-reducer");
var useDataflowOfZoom = function (zoomable$, mouseLeftIsDown$, mouseRightIsDown$, wheelScrollDelta$, mode$, imgVisibleAreaRectAbsolute$, pointerPosOnImgVisibleArea$, resetZoom$, fitPosToGrid) {
    /**
     * @description
     * zoom in or zoom out by v% per scroll count
     */
    var zoomRatioDelta$ = (0, utils_1.useStream)(wheelScrollDelta$.pipe((0, utils_1.filterByLatest)(zoomable$), (0, operators_1.map)(wheel_scroll_delta_1.wheelScrollDeltaToCount), (0, operators_1.map)(function (v) { return (v > 0 ? 1 + v / 100 : 1 / (1 - v / 100)); }), (0, operators_1.startWith)(1)));
    var readonly$ = (0, utils_1.useDataStream)(false, mode$.pipe((0, utils_1.valueIs)('readonly')));
    /**
     * @description 拡大率（1以上）
     */
    var zoomRatio$ = (0, utils_1.useDataStream)(1, (0, utils_1.mergeTyped)(zoomRatioDelta$.pipe((0, operators_1.map)(function (d) { return ({ op: 'update', delta: d }); }), (0, utils_1.filterByLatest)(readonly$.pipe((0, utils_1.valueIs)(false)))), resetZoom$.pipe((0, operators_1.mapTo)({ op: 'reset' })), readonly$.pipe((0, operators_1.mapTo)({ op: 'reset' }))).pipe((0, operators_1.scan)(zoom_ratio_reducer_1.zoomRatioReducer, 1)));
    /**
     * @description
     * マウスポインタ位置（imgVisibleAreaRectAbsoluteを1x1とする相対値）
     */
    var pointerPosRelative$ = (0, utils_1.useDataStream)(undefined, (0, utils_1.combineLatestTyped)(imgVisibleAreaRectAbsolute$.pipe((0, operators_1.map)(function (rect) {
        return (0, utils_1.toRelativePos)(rect.withMutations(function (r) { return r.set('left', 0).set('top', 0); }));
    })), pointerPosOnImgVisibleArea$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), toRelativePosFn = _b[0], pointerPosOnVisibleArea = _b[1];
        return pointerPosOnVisibleArea === undefined
            ? undefined
            : fitPosToGrid(toRelativePosFn(pointerPosOnVisibleArea));
    })));
    /**
     * @description
     * マウスポインタ位置の変化量（imgVisibleAreaRectAbsoluteを1x1とする相対値）
     */
    var pointerPosDeltaRelative$ = (0, utils_1.useStream)(pointerPosRelative$.pipe((0, utils_1.filterNotUndefined)(), (0, operators_1.startWith)((0, utils_1.IPos)()), (0, operators_1.pairwise)(), (0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), prev = _b[0], curr = _b[1];
        return (0, utils_1.IPos)({
            x: curr.x - prev.x,
            y: curr.y - prev.y
        });
    })));
    var dragImgDeltaRelative$ = (0, utils_1.useStream)(pointerPosDeltaRelative$.pipe((0, utils_1.filterByAll)((0, utils_1.combineLatestTyped)(mouseRightIsDown$, mouseLeftIsDown$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), left = _b[0], right = _b[1];
        return left || right;
    })), mode$.pipe((0, operators_1.map)(function (e) { return e === 'move-image-standby' || e === 'move-image-dragging'; })))));
    /**
     * @description
     * 画像をx/y方向にどれだけ動かしているか（imgVisibleAreaRectAbsoluteを1x1とする相対値）
     */
    var imgMoveAmountRelative$ = (0, utils_1.useDataStream)((0, utils_1.IVector2d)(), (0, utils_1.mergeTyped)(zoomRatioDelta$.pipe((0, operators_1.map)(function (e) { return ({ op: 'zoom', delta: e }); })), dragImgDeltaRelative$.pipe((0, operators_1.map)(function (e) { return ({ op: 'drag', delta: e }); })), resetZoom$.pipe((0, operators_1.mapTo)({ op: 'reset' })), readonly$.pipe((0, operators_1.mapTo)({ op: 'reset' }))).pipe((0, utils_1.withLatestValuesFrom)(pointerPosRelative$, zoomRatio$), (0, operators_1.scan)(img_move_reducer_1.imgMoveReducer, (0, utils_1.IVector2d)())));
    /**
     * @description 画像表示領域の左上を原点としたときの拡大後の画像の仮想的なサイズ．
     * @example
     *   画像を中心を固定して2倍に拡大しているとき，
     *   { top: -0.5, left: -0.5, width: 2, height: 2 }
     *   となる．
     */
    var virtualImgRectRelative$ = (0, utils_1.useDataStream)((0, utils_1.IRect)(), (0, utils_1.combineLatestTyped)(zoomRatio$, imgMoveAmountRelative$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), zoomRatio = _b[0], moveAmountRelative = _b[1];
        return (0, utils_1.IRect)({
            top: moveAmountRelative.y,
            left: moveAmountRelative.x,
            width: zoomRatio,
            height: zoomRatio
        });
    }), (0, operators_1.filter)(utils_1.isValidRect)));
    /**
     * @description 画像表示領域の左上を原点としたときの拡大後の画像の仮想的なサイズ．
     * @example
     *   imgVisibleAreaRectAbsolute = { width: 150, height: 100 }
     *   virtualImgRectAbsolute = { top: -0.5, left: -0.5, width: 2, height: 2 }
     *   のとき，
     *   { top: -50, left: -75, width: 300, height: 200 }
     *   となる．
     *
     *     virtualImgRectAbsolute
     *   +----------------------------------------------+
     *   |                      ^                       |
     *   |                  -50 |                       |
     *   |                      |                       |
     *   |           +----------------------+           |
     *   |           |                      |           |
     *   |           |                      |           |
     *   |<----------|    visible area      |100        |200
     *   |   -75     |                      |           |
     *   |           |                      |           |
     *   |           |                      |           |
     *   |           +----------------------+           |
     *   |                     150                      |
     *   |                                              |
     *   |                                              |
     *   +----------------------------------------------+
     *                         300
     */
    var virtualImgRectAbsolute$ = (0, utils_1.useDataStream)((0, utils_1.IRect)(), (0, utils_1.combineLatestTyped)(imgVisibleAreaRectAbsolute$, virtualImgRectRelative$).pipe((0, operators_1.map)(function (_a) {
        var _b = tslib_1.__read(_a, 2), imgVisibleAreaRectAbsolute = _b[0], virtualImgRectRelative = _b[1];
        return (0, utils_1.IRect)({
            top: virtualImgRectRelative.top * imgVisibleAreaRectAbsolute.height,
            left: virtualImgRectRelative.left * imgVisibleAreaRectAbsolute.width,
            width: virtualImgRectRelative.width * imgVisibleAreaRectAbsolute.width,
            height: virtualImgRectRelative.height * imgVisibleAreaRectAbsolute.height
        });
    })));
    return virtualImgRectAbsolute$;
};
exports.useDataflowOfZoom = useDataflowOfZoom;
